import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, User, Lock, Eye, EyeOff, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LoginHero } from "@/components/login/LoginHero";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const auth = useAuth();
  const nav = useNavigate();

  const canSubmit = username.trim() !== "" && password !== "" && !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setLoading(true);
    try {
      await auth.login(username.trim(), password, remember);
      nav("/dashboard");
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid username or password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = (invalid) =>
    `w-full pl-11 pr-11 py-3.5 rounded-lg bg-white border ${invalid ? "border-[#B91C1C]" : "border-slate-200"} text-[#1F2937] placeholder-slate-400 text-sm outline-none focus:ring-2 focus:ring-[#008080] focus:border-transparent transition-all duration-200`;

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row font-sans" data-testid="login-page">
      <LoginHero />

      {/* RIGHT PANEL — SIGN-IN CARD */}
      <div className="order-1 lg:order-2 lg:w-[40%] flex items-center justify-center bg-[#003366] p-4 md:p-6 lg:p-8" data-testid="signin-panel">
        <div className="bg-[#F8F9FA] rounded-3xl shadow-2xl p-6 lg:p-10 w-full max-w-[480px]" data-testid="signin-card">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#008080] mb-2">Sign in</div>
          <h1 className="text-3xl font-bold text-[#1F2937]">Welcome back</h1>
          <p className="text-slate-500 mt-2" data-testid="signin-workspace-description">Sign in to your DPDP Workspace</p>

          {error && (
            <div
              role="alert"
              className="mt-6 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-[#B91C1C]"
              data-testid="login-error-banner"
            >
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={submit} noValidate className="mt-8 space-y-5">
            <div>
              <label htmlFor="username" className="block text-xs font-semibold uppercase tracking-wider text-[#1F2937] mb-2">
                Username
              </label>
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  id="username"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); setError(""); }}
                  placeholder="Enter your username"
                  autoComplete="username"
                  className={inputCls(!!error)}
                  data-testid="login-username-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-semibold uppercase tracking-wider text-[#1F2937] mb-2">
                Password
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(""); }}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  className={inputCls(!!error)}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 rounded focus:outline-none focus:ring-2 focus:ring-[#008080]"
                  data-testid="password-toggle-btn"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {error && <p className="text-[#B91C1C] text-xs mt-1">Check your username and password.</p>}
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 accent-[#008080] focus:outline-none focus:ring-2 focus:ring-[#008080]"
                  data-testid="remember-me-checkbox"
                />
                Remember me
              </label>
              <a
                href="/forgot-password"
                className="text-[#008080] font-semibold hover:underline focus:outline-none focus:ring-2 focus:ring-[#008080] rounded"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </a>
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full mt-6 flex items-center justify-center gap-2 py-3.5 rounded-lg bg-[#003366] hover:bg-[#002855] text-white font-semibold shadow-lg shadow-[#003366]/25 hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#008080] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:bg-[#003366]"
              data-testid="login-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          {/* TRUST LINE */}
          <div className="mt-8 border-t border-slate-100 pt-6">
            <div className="flex items-center justify-center gap-2">
              <ShieldCheck size={20} className="text-[#008080] shrink-0" />
              <span className="font-semibold text-sm text-[#1F2937]">
                Audit-ready evidence, continuously maintained.
              </span>
            </div>
            <p className="text-center text-xs text-slate-500 mt-1">Secure. Reliable. Built for DPDP.</p>
          </div>

          {/* NEW-USER CONVERSION PATH */}
          <div className="mt-6 pt-6 border-t border-slate-100 text-center" data-testid="new-user-cta">
            <p className="text-sm text-slate-500">Not yet completed DPDP Gap assessment?</p>
            <a
              href="https://dpdp.codexero.ai/request-access"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-1.5 text-[#008080] font-semibold text-sm hover:underline focus:outline-none focus:ring-2 focus:ring-[#008080] rounded"
              data-testid="start-assessment-link"
            >
              Start your Executive readiness assessment
              <ArrowRight size={15} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
